import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import api from '../services/api';

/**
 * Android push notifications (Firebase Cloud Messaging). When the admin sends a
 * notification, the backend pushes it to every registered phone, so it pops up in the
 * Android notification shade like any other app — even with the app closed.
 *
 * Gated by VITE_PUSH_ENABLED=true (frontend/.env): set it only AFTER
 * android/app/google-services.json is in place — calling Firebase without that file
 * crashes the Android app.
 *
 * Two switches must both be on for a popup: Android's own notification permission
 * for the app, and the in-app "Push notifications" switch (menu), stored per device.
 */

const PREF_KEY = 'sx_push_enabled';
const TOKEN_KEY = 'sx_push_token';
/** Must match PushNotificationService.CHANNEL_ID on the backend. */
const CHANNEL_ID = 'stockxignal_alerts';

export function isPushSupported() {
  return Capacitor.isNativePlatform()
    && Capacitor.getPlatform() === 'android'
    && String(import.meta.env.VITE_PUSH_ENABLED) === 'true';
}

/** In-app switch; defaults to ON until the user turns it off. */
export function isPushPrefOn() {
  try { return localStorage.getItem(PREF_KEY) !== 'false'; } catch { return true; }
}

let listenersAdded = false;

async function addListeners(onOpenRoute) {
  if (listenersAdded) return;
  listenersAdded = true;
  await PushNotifications.addListener('registration', ({ value }) => {
    try { localStorage.setItem(TOKEN_KEY, value); } catch { /* ignore */ }
    api.post('/push/register', { token: value, platform: 'android' }).catch(() => {});
  });
  await PushNotifications.addListener('registrationError', (err) => {
    console.warn('Push registration failed', err);
  });
  // Tapping the popup opens the app on the Notifications tab
  await PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
    const route = action?.notification?.data?.route || '/notifications';
    if (onOpenRoute) onOpenRoute(route);
  });
  // App already open: Android doesn't show the popup, so refresh the in-app feed instead
  await PushNotifications.addListener('pushNotificationReceived', () => {
    window.dispatchEvent(new Event('sx-notification-pushed'));
  });
}

/**
 * Asks Android for permission (first time only) and registers this phone.
 * Returns { ok: true } or { ok: false, reason: 'unsupported' | 'denied' | 'error' }.
 */
export async function enablePush(onOpenRoute) {
  if (!isPushSupported()) return { ok: false, reason: 'unsupported' };
  try {
    await addListeners(onOpenRoute);
    let perm = await PushNotifications.checkPermissions();
    if (perm.receive === 'prompt' || perm.receive === 'prompt-with-rationale') {
      perm = await PushNotifications.requestPermissions();
    }
    if (perm.receive !== 'granted') return { ok: false, reason: 'denied' };
    // High importance = heads-up popup (not just a silent icon in the status bar)
    await PushNotifications.createChannel({
      id: CHANNEL_ID,
      name: 'StockXignal alerts',
      description: 'Notifications sent by StockXignal',
      importance: 5,
      visibility: 1,
      vibration: true,
    });
    await PushNotifications.register();
    try { localStorage.setItem(PREF_KEY, 'true'); } catch { /* ignore */ }
    return { ok: true };
  } catch (e) {
    console.warn('Push enable failed', e);
    return { ok: false, reason: 'error' };
  }
}

/** In-app switch turned off: stop pushes to this phone. */
export async function disablePush() {
  try { localStorage.setItem(PREF_KEY, 'false'); } catch { /* ignore */ }
  await forgetDevice();
}

/** On logout (call BEFORE clearing the auth token): this phone stops getting pushes
 *  for the logged-out account; the in-app switch preference is kept. */
export async function forgetDevice() {
  if (!isPushSupported()) return;
  let token = null;
  try { token = localStorage.getItem(TOKEN_KEY); } catch { /* ignore */ }
  if (token) await api.post('/push/unregister', { token }).catch(() => {});
  try { localStorage.removeItem(TOKEN_KEY); } catch { /* ignore */ }
  try { await PushNotifications.unregister(); } catch { /* ignore */ }
}
